import { forwardRef, useId, useState, useEffect, useRef, useLayoutEffect } from 'react';
import styled from '@emotion/styled';

import { FieldLabel } from '../FieldLabel';
import { Message } from '../Message';

import type { TextAreaProps, TextAreaResize, TextAreaFontSize } from './TextArea.types';
import type { Theme } from '../../theme';

/**
 * Wrapper container for the entire component
 */
const Wrapper = styled.div<{ $fullWidth: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  ${({ $fullWidth }) => $fullWidth && 'width: 100%;'}
`;

/**
 * Container for the textarea with border styling
 */
const TextAreaContainer = styled.div<{
  theme?: Theme;
  $disabled: boolean;
  $error: boolean;
  $noBorder: boolean;
  $focused: boolean;
  $focusColor?: string;
}>`
  display: flex;
  flex-direction: column;
  width: 100%;
  border: ${({ $noBorder }) => ($noBorder ? 'none' : '1px solid')};
  border-color: ${({ theme, $error, $focused, $disabled, $focusColor }) => {
    if ($disabled) return theme.colors.grey[200];
    if ($error) return theme.colors.semantic.red.base;
    if ($focused) return $focusColor || theme.colors.grey[900];
    return theme.colors.grey[300];
  }};
  box-sizing: border-box;
  border-radius: 4px;
  overflow: hidden;
  transition: border-color 0.1s ease, box-shadow 0.1s ease;
  background-color: ${({ theme, $disabled }) =>
    $disabled ? theme.colors.grey[100] : theme.colors.white};

  /* Hover effect */
  ${({ $disabled, $noBorder, theme, $error, $focusColor }) =>
    !$disabled &&
    !$noBorder &&
    `
    &:hover {
      border-color: ${$error ? theme.colors.semantic.red.base : ($focusColor || theme.colors.grey[400])};
    }
  `}

  /* Focus effect */
  ${({ $focused, $noBorder, theme, $error, $focusColor }) =>
    $focused &&
    !$noBorder &&
    `
    border-color: ${$error ? theme.colors.semantic.red.base : ($focusColor || theme.colors.grey[900])} !important;
    ${$focusColor && !$error ? `box-shadow: 0 0 0 2px ${$focusColor}20;` : ''}
  `}
`;

/**
 * Character count display below the textarea
 */
const CharacterCount = styled.div<{
  theme?: Theme;
}>`
  display: flex;
  justify-content: flex-end;
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-size: ${({ theme }) => theme.typography.fontSize.tn};
  color: ${({ theme }) => theme.colors.text.muted};
  margin-top: 4px;
`;

/**
 * The actual textarea element
 */
const StyledTextArea = styled.textarea<{
  theme?: Theme;
  $resize: TextAreaResize;
  $disabled: boolean;
  $fontSize?: TextAreaFontSize;
}>`
  width: 100%;
  border: none;
  padding: 8px 12px;
  box-sizing: border-box;
  overflow: auto;
  min-height: 34px;
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-size: ${({ theme, $fontSize }) => theme.typography.fontSize[$fontSize || 'sm']};
  line-height: ${({ theme }) => theme.typography.lineHeight.normal};
  color: ${({ theme }) => theme.colors.text.primary};
  background: transparent;

  /* Resize behavior */
  resize: ${({ $resize }) => $resize};

  &::placeholder {
    color: ${({ theme, $disabled }) =>
      $disabled ? theme.colors.grey[300] : theme.colors.grey[400]};
    opacity: 1;
  }

  &:disabled {
    cursor: not-allowed;
    background-color: transparent;
  }

  &:focus {
    outline: none;
  }
`;

/**
 * TextArea - A multi-line text input component
 *
 * Supports draggable resize, auto-expansion, and integration with form messages.
 * Can be controlled or uncontrolled.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <TextArea
 *   label="Description"
 *   placeholder="Enter a description..."
 * />
 *
 * // With auto-expansion
 * <TextArea
 *   label="Comments"
 *   rows={3}
 *   maxRows={10}
 *   resize="none"
 * />
 *
 * // With error state
 * <TextArea
 *   label="Bio"
 *   error
 *   errorMessage="Bio is required"
 * />
 *
 * // Draggable resize
 * <TextArea
 *   label="Notes"
 *   resize="vertical"
 *   rows={4}
 * />
 * ```
 */
export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      value: controlledValue,
      defaultValue = '',
      onChange,
      placeholder,
      label,
      rows = 3,
      maxRows,
      disabled = false,
      readOnly = false,
      required = false,
      error = false,
      errorMessage,
      assistiveMessage,
      resize = 'vertical',
      noBorder = false,
      name,
      id: providedId,
      'aria-label': ariaLabel,
      className,
      fullWidth = true,
      'data-testid': testId,
      dataTestId,
      dataId,
      maxLength,
      showCharCount = false,
      onFocus,
      onBlur,
      onKeyDown,
      focusColor,
      fontSize,
    },
    ref
  ) => {
    const generatedId = useId();
    const textareaId = providedId || generatedId;
    const errorId = `${textareaId}-error`;
    const assistiveId = `${textareaId}-assistive`;

    const internalRef = useRef<HTMLTextAreaElement>(null);
    const textareaRef = (ref as React.RefObject<HTMLTextAreaElement>) || internalRef;

    // Determine if controlled or uncontrolled
    const isControlled = controlledValue !== undefined;
    const [internalValue, setInternalValue] = useState(defaultValue);
    const currentValue = isControlled ? controlledValue : internalValue;

    const [isFocused, setIsFocused] = useState(false);
    const [currentRows, setCurrentRows] = useState(rows);

    // Sync internal state with controlled value
    useEffect(() => {
      if (isControlled) {
        setInternalValue(controlledValue);
      }
    }, [isControlled, controlledValue]);

    // Auto-adjust rows based on content
    const adjustRows = () => {
      const textarea = textareaRef.current;
      if (!textarea || !maxRows) return;

      // Reset to minimum rows to get accurate scrollHeight
      textarea.rows = 1;

      const computedStyles = window.getComputedStyle(textarea);
      const lineHeight = parseInt(computedStyles.lineHeight.replace(/\D/g, ''), 10) || 20;
      const calculatedRows = Math.floor(textarea.scrollHeight / lineHeight);

      const newRows = Math.min(Math.max(calculatedRows, rows), maxRows);
      textarea.rows = newRows;
      setCurrentRows(newRows);
    };

    // Adjust rows on mount and when value changes
    useLayoutEffect(() => {
      if (maxRows) {
        adjustRows();
      }
    }, [currentValue, maxRows]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (!isControlled) {
        setInternalValue(event.target.value);
      }

      if (maxRows) {
        adjustRows();
      }

      onChange?.(event);
    };

    const handleFocus = (event: React.FocusEvent<HTMLTextAreaElement>) => {
      setIsFocused(true);
      onFocus?.(event);
    };

    const handleBlur = (event: React.FocusEvent<HTMLTextAreaElement>) => {
      setIsFocused(false);
      onBlur?.(event);
    };

    // Build aria-describedby
    const describedBy = [
      error && errorMessage ? errorId : undefined,
      !error && assistiveMessage ? assistiveId : undefined,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <Wrapper className={className} $fullWidth={fullWidth} data-testid={dataTestId || testId} data-id={dataId}>
        {label && (
          <FieldLabel
            label={typeof label === 'string' ? label : ''}
            htmlFor={textareaId}
            required={required}
          />
        )}
        <TextAreaContainer
          $disabled={disabled}
          $error={error}
          $noBorder={noBorder}
          $focused={isFocused}
          $focusColor={focusColor}
          data-testid={testId && `${testId}-container`}
        >
          <StyledTextArea
              ref={textareaRef}
              id={textareaId}
              name={name}
              value={currentValue}
              onChange={handleChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onKeyDown={onKeyDown}
              placeholder={placeholder}
              rows={maxRows ? currentRows : rows}
              disabled={disabled}
              readOnly={readOnly}
              required={required}
              maxLength={maxLength}
              aria-label={!label ? ariaLabel : undefined}
              aria-invalid={error}
              aria-describedby={describedBy || undefined}
              aria-required={required}
              $resize={maxRows ? 'none' : resize}
              $disabled={disabled}
              $fontSize={fontSize}
              data-testid={testId && `${testId}-input`}
            />
        </TextAreaContainer>
        {error && errorMessage ? (
          <Message variant="error" size="sm" dataTestId={testId ? `${testId}-error` : undefined}>
            {errorMessage}
          </Message>
        ) : assistiveMessage ? (
          <Message variant="assistive" size="sm" dataTestId={testId ? `${testId}-assistive` : undefined}>
            {assistiveMessage}
          </Message>
        ) : null}
        {showCharCount && (
          <CharacterCount data-testid={testId ? `${testId}-char-count` : undefined}>
            {maxLength
              ? `${(currentValue as string).length.toLocaleString()} / ${maxLength.toLocaleString()} characters`
              : `${(currentValue as string).length.toLocaleString()} characters`}
          </CharacterCount>
        )}
      </Wrapper>
    );
  }
);

TextArea.displayName = 'TextArea';

export default TextArea;
